import { Functions as FunctionsIcon } from "@mui/icons-material";
import { Stack } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid-premium";
import React from "react";
import type { EstimatedCalculation__ItemFragment } from "../../doc-items/boxes/EstimatedCalculationBox.generated.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import type { DocDataGridRow } from "../types.js";

type ExpressionResult =
  | { __type: "missing-value" }
  | { __type: "value"; value: unknown }
  | { __type: "no_expr" }
  | { __type: "error"; message: string };

interface Expression {
  attribute: string;
  expr: string;
  result: ExpressionResult;
  missingValue: boolean;
}

interface DisplayProps<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
> {
  params: GridRenderCellParams<DocDataGridRow<T>>;
  fieldName: string;
  formattedValue: string;
}

/**
 * Read-only display — shows the formatted value, with a non-interactive
 * formula icon as a visual cue when an attribute expression is set. The
 * icon is NOT clickable here; opening the formula editor is the edit-cell's
 * responsibility (see `estimatedQuantityColumn`'s `renderEditCell`, which
 * mounts `AttributeExpressionModal` when the user enters edit mode on a
 * cell that already carries an expression). When no expression is set the
 * cell renders as plain text — the "add formula" affordance lives in the
 * edit cell so it appears only while the user is actively editing.
 */
export const DataGridFloatWithExpressionDisplay = <
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>({
  params,
  fieldName,
  formattedValue,
}: DisplayProps<T>) => {
  const item = params.row.item as EstimatedCalculation__ItemFragment | null;
  const expression: Expression | undefined = item?.attributeExpressions?.find(
    e => e.attribute === fieldName
  ) as Expression | undefined;

  // `result` arrives from the `JSON` scalar as `any`, so guard the read.
  const hasExpressionRefError = expression?.result?.__type === "missing-value";

  if (!expression) {
    return <span>{formattedValue}</span>;
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ alignItems: "center", height: "100%" }}
    >
      <FunctionsIcon
        color="secondary"
        sx={{ fontSize: "1rem", flexShrink: 0 }}
      />
      {hasExpressionRefError ? (
        <span style={{ color: "red" }}>{"Reference error"}</span>
      ) : (
        <span>{formattedValue}</span>
      )}
    </Stack>
  );
};
